import React from 'react';
import { AppTextField, AppDateField, AppIconButton } from '../../../../../Components/FormKit';
import { AppTableCard } from '../../../../../Components/TableKit';

// ─── Design tokens ────────────────────────────────────────────────────────────
const TEAL = '#00897B';

// ─── TableEditor ──────────────────────────────────────────────────────────────
const TableEditor = ({ value = [], columns = [], onChange, disabled = false }) => {
    const rows = Array.isArray(value) ? value : [];

    const handleCellChange = (rowIdx, colKey, cellValue) => {
        const updated = rows.map((row, i) =>
            i === rowIdx ? { ...row, [colKey]: cellValue } : row
        );
        onChange(updated);
    };

    const addRow = () => {
        const newRow = {};
        columns.forEach(c => { newRow[c.key] = c.type === 'boolean' ? false : ''; });
        onChange([...rows, newRow]);
    };

    const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

    return (
        <div>
            <AppTableCard animated={false}>
                <table className="tk-table tk-table--compact tk-table--striped">
                    <thead>
                        <tr>
                            {columns.map(col => (
                                <th key={col.key}>
                                    {col.label}
                                </th>
                            ))}
                            {!disabled && (
                                <th style={{ width: '2rem' }} />
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => (
                            <tr key={i}>
                                {columns.map(col => (
                                    <td key={col.key} style={{ padding: '0.3rem 0.4rem' }}>
                                        {col.type === 'boolean' ? (
                                            <input type="checkbox"
                                                   checked={!!row[col.key]}
                                                   disabled={disabled}
                                                   onChange={e => handleCellChange(i, col.key, e.target.checked)} />
                                        ) : (
                                            <input type="text"
                                                   value={row[col.key] ?? ''}
                                                   disabled={disabled}
                                                   onChange={e => handleCellChange(i, col.key, e.target.value)}
                                                   style={{ width: '100%', border: '1px solid #e5e7eb',
                                                            borderRadius: '4px', padding: '0.25rem 0.4rem',
                                                            fontSize: '0.8rem', outline: 'none',
                                                            boxSizing: 'border-box' }} />
                                        )}
                                    </td>
                                ))}
                                {!disabled && (
                                    <td style={{ padding: '0.3rem' }}>
                                        <AppIconButton icon="pi pi-trash" danger size="sm" onClick={() => removeRow(i)} />
                                    </td>
                                )}
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={columns.length + (disabled ? 0 : 1)}
                                    className="text-center text-xs text-400 font-italic"
                                    style={{ padding: '0.6rem' }}>
                                    Sin registros
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </AppTableCard>
            {!disabled && (
                <button onClick={addRow}
                        style={{ marginTop: '0.4rem', background: 'none',
                                 border: `1px dashed ${TEAL}`, borderRadius: '4px',
                                 padding: '0.25rem 0.6rem', fontSize: '0.75rem',
                                 color: TEAL, cursor: 'pointer', fontWeight: 600 }}>
                    <i className="pi pi-plus mr-1" style={{ fontSize: '0.65rem' }} />
                    Agregar fila
                </button>
            )}
        </div>
    );
};

// ─── ListEditor ───────────────────────────────────────────────────────────────
const ListEditor = ({ value = [], onChange, disabled = false }) => {
    const items = Array.isArray(value)
        ? value.map(v => typeof v === 'string' ? v : (v?.nombre || v?.name || JSON.stringify(v)))
        : [];

    const handleChange = (idx, newVal) => {
        const updated = items.map((item, i) => i === idx ? newVal : item);
        onChange(updated);
    };

    const addItem = () => onChange([...items, '']);
    const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));

    return (
        <div className="flex flex-column gap-1">
            {items.map((item, i) => (
                <div key={i} className="flex align-items-center gap-1">
                    <input type="text"
                           value={item}
                           disabled={disabled}
                           onChange={e => handleChange(i, e.target.value)}
                           style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '4px',
                                    padding: '0.3rem 0.5rem', fontSize: '0.8rem', outline: 'none' }} />
                    {!disabled && (
                        <AppIconButton icon="pi pi-trash" danger size="sm" onClick={() => removeItem(i)} />
                    )}
                </div>
            ))}
            {items.length === 0 && (
                <span className="text-xs text-400 font-italic">Sin registros</span>
            )}
            {!disabled && (
                <button onClick={addItem}
                        style={{ alignSelf: 'flex-start', background: 'none',
                                 border: `1px dashed ${TEAL}`, borderRadius: '4px',
                                 padding: '0.2rem 0.5rem', fontSize: '0.75rem',
                                 color: TEAL, cursor: 'pointer', fontWeight: 600 }}>
                    <i className="pi pi-plus mr-1" style={{ fontSize: '0.6rem' }} />
                    Agregar
                </button>
            )}
        </div>
    );
};

// ─── StructuredLegalEditor ────────────────────────────────────────────────────
const StructuredLegalEditor = ({
    schema,
    editValues = {},
    activeOverridePaths = [],
    accentColor = TEAL,
    onFieldChange,
    onClearPath,
    disabled = false,
}) => {
    if (!schema) return null;

    const renderField = (field) => {
        const isConfirmed = activeOverridePaths.includes(field.path);
        const val = editValues[field.path];

        const header = (
            <div className="flex align-items-center gap-1 mb-1">
                <span className="text-xs text-500 font-semibold"
                      style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {field.label}
                </span>
                {isConfirmed && (
                    <span className="text-xs font-semibold ml-1"
                          style={{ color: accentColor, background: `${accentColor}15`,
                                   padding: '0 0.35rem', borderRadius: '4px' }}>
                        Confirmado
                    </span>
                )}
                {isConfirmed && onClearPath && !disabled && (
                    <AppIconButton icon="pi pi-undo" title="Revertir al valor de IA"
                        onClick={() => onClearPath(field.path)} size="sm" />
                )}
            </div>
        );

        switch (field.type) {
            case 'table':
                return (
                    <div key={field.path} className="mb-3">
                        {header}
                        <TableEditor
                            value={val}
                            columns={field.columns}
                            onChange={v => onFieldChange(field.path, v)}
                            disabled={disabled}
                        />
                    </div>
                );
            case 'list':
                return (
                    <div key={field.path} className="mb-3">
                        {header}
                        <ListEditor
                            value={val}
                            onChange={v => onFieldChange(field.path, v)}
                            disabled={disabled}
                        />
                    </div>
                );
            default: { // 'text', 'date'
                const trailing = (
                    <>
                        {isConfirmed && (
                            <span className="text-xs font-semibold ml-1"
                                  style={{ color: accentColor, background: `${accentColor}15`,
                                           padding: '0 0.35rem', borderRadius: '4px' }}>
                                Confirmado
                            </span>
                        )}
                        {isConfirmed && onClearPath && !disabled && (
                            <AppIconButton icon="pi pi-undo" title="Revertir al valor de IA"
                                onClick={() => onClearPath(field.path)} size="sm" />
                        )}
                    </>
                );
                const cssVars = isConfirmed
                    ? { '--fk-border': accentColor, '--fk-bg': `${accentColor}08` }
                    : undefined;

                if (field.type === 'date') {
                    return (
                        <div key={field.path} style={cssVars}>
                            <AppDateField
                                label={field.label}
                                value={val ?? ''}
                                onChange={e => onFieldChange(field.path, e.value)}
                                disabled={disabled}
                                trailing={trailing}
                            />
                        </div>
                    );
                }
                return (
                    <div key={field.path} style={cssVars}>
                        <AppTextField
                            label={field.label}
                            value={val ?? ''}
                            onChange={e => onFieldChange(field.path, e.target.value)}
                            disabled={disabled}
                            trailing={trailing}
                        />
                    </div>
                );
            }
        }
    };

    return (
        <div className="fk-root">
            {schema.sections.map(section => {
                const scalarFields = section.fields.filter(f => f.type === 'text' || f.type === 'date');
                const structuredFields = section.fields.filter(f => f.type === 'table' || f.type === 'list');

                return (
                    <div key={section.section} className="mb-3">
                        <div className="text-xs text-500 font-semibold mb-2"
                             style={{ textTransform: 'uppercase', letterSpacing: '0.06em',
                                      paddingBottom: '0.3rem',
                                      borderBottom: '1px solid #e5e7eb' }}>
                            {section.section}
                        </div>
                        {scalarFields.length > 0 && (
                            <div style={{ display: 'grid',
                                          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                          gap: '0.6rem 1rem' }}
                                 className="mb-2">
                                {scalarFields.map(renderField)}
                            </div>
                        )}
                        {structuredFields.map(renderField)}
                    </div>
                );
            })}
        </div>
    );
};

export { TableEditor, ListEditor, StructuredLegalEditor };
export default StructuredLegalEditor;
